import React from 'react';
import { useSelector } from 'react-redux';
import { useAssumptions } from './useAssumptions';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { GeneralAssumptions } from '../../../../components/GeneralAssumptions/GeneralAssumptions';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import formatAsCurrency from '../../../../../../CommonUtils/formatAsCurrency';
import { selectIsCanadian } from '../../../../../../CommonSelectors/CommonAreaSelector';

export const Assumptions: React.FC = () => {
  const { data } = useAssumptions();

  const isCanadian = useSelector(selectIsCanadian);

  if (!data) {
    return null;
  }

  return (
    <Box>
      <HtmlTable>
        <table>
          <colgroup>
            <col width="50%" />
            <col width="50%" />
          </colgroup>

          <caption>Retirement Goal Assumptions</caption>

          <thead>
            <tr>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Assumption
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Value
                </Text>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <Text as="td" fontWeight={500}>
                Retirement Age
              </Text>

              <Box as="td">
                {data.RetirementAge.map(({ Name, Age }) => (
                  <Text key={Name} fontWeight={500}>
                    {Name}: Age {Age}
                  </Text>
                ))}
              </Box>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Retirement Living Expenses
              </Text>

              <Text as="td" fontWeight={500}>
                {formatCurrency(data.RetirementLivingExpenses.amount)}{' '}
                {data.RetirementLivingExpenses.frequency}
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Life Expectancy
              </Text>

              <Box as="td">
                {data.LifeExpectancy.map(({ Name, Age }) => (
                  <Text key={Name} fontWeight={500}>
                    {Name}: Age {Age}
                  </Text>
                ))}
              </Box>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Additional Saving Per Month
              </Text>

              <Text as="td" fontWeight={500}>
                {formatCurrency(data.AdditionalSavingPerMonth)} per month
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Pre-Retirement Asset Allocation
              </Text>

              <Text as="td" fontWeight={500}>
                {data.PreRetirementAssetAllocation}
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Pre-Retirement Rate of Return
              </Text>

              <Text as="td" fontWeight={500}>
                {Math.round(data.PreRetirementRateOfReturn * 100 * 100) / 100}%
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Pre-Retirement Standard Deviation
              </Text>

              <Text as="td" fontWeight={500}>
                {Math.round(data.PreRetirementStandardDeviation * 100 * 100) / 100}%
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Post-Retirement Asset Allocation
              </Text>

              <Text as="td" fontWeight={500}>
                {data.PostRetirementAssetAllocation}
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Post-Retirement Rate of Return
              </Text>

              <Text as="td" fontWeight={500}>
                {Math.round(data.PostRetirementRateOfReturn * 100 * 100) / 100}%
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Post-Retirement Standard Deviation
              </Text>

              <Text as="td" fontWeight={500}>
                {Math.round(data.PostRetirementStandardDeviation * 100 * 100) / 100}%
              </Text>
            </tr>

            {!isCanadian && (
              <>
                <tr>
                  <Text as="td" fontWeight={500}>
                    Social Security Filing Age
                  </Text>

                  <Box as="td">
                    {data.SocialSecurityFilingAge.map(({ Name, Age }) => (
                      <Text key={Name} fontWeight={500}>
                        {Name}: Age {Age}
                      </Text>
                    ))}
                  </Box>
                </tr>

                <tr>
                  <Text as="td" fontWeight={500}>
                    Social Security Amount
                  </Text>

                  <Box as="td">
                    {data.SocialSecurityAmount.map(({ Name, Amount }) => (
                      <Text key={Name} fontWeight={500}>
                        {Name}: {formatCurrency(Amount)}
                      </Text>
                    ))}
                  </Box>
                </tr>

                <tr>
                  <Text as="td" fontWeight={500}>
                    Social Security Inflation Rate
                  </Text>

                  <Text as="td" fontWeight={500}>
                    {Math.round(data.SocialSecurityInflationRate * 100 * 100) / 100}%
                  </Text>
                </tr>
              </>
            )}

            <tr>
              <Text as="td" fontWeight={500}>
                Inflation Rate
              </Text>

              <Text as="td" fontWeight={500}>
                {Math.round(data.InflationRate * 100 * 100) / 100}%
              </Text>
            </tr>

            {data.MarketDrop && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Market Drop
                </Text>

                <Text as="td" fontWeight={500}>
                  {data.MarketDrop.marketDropsPercentage === 0
                    ? 'No market drop applied'
                    : `${Math.round(data.MarketDrop.marketDropsPercentage * 100 * 100) /
                        100}% drop when ${data.MarketDrop.primary} is Age ${
                        data.MarketDrop.marketDropsStartingAge
                      } for ${data.MarketDrop.marketDropsNumYears} Years`}
                </Text>
              </tr>
            )}

            <tr>
              <Text as="td" fontWeight={500}>
                Earmarked Assets
              </Text>

              <Text as="td" fontWeight={500}>
                Total Earmarked toward this goal {formatAsCurrency(data.EarmarkedAssets)}
              </Text>
            </tr>
          </tbody>
        </table>
      </HtmlTable>

      <GeneralAssumptions mt="24" />
    </Box>
  );
};
